import React, { useState, useEffect, useRef } from "react";
import ActionsModal from "../ActionsModal";
import "./index.css";
import { FaEdit, FaTrash } from "react-icons/fa";
import Select from "react-select";
import GeneralToast from "../../Components/Toaster";
import { OnStatusOptions } from "../../Utils/constants";
import { useAssetNameSearch } from "../../API/Queries/useAssetNameSearch";

const AssetsModal = ({ onClose, onSaveAsset, existingAsset = null }) => {
  const [form, setForm] = useState({
    id: "",
    name: "",
    defaultActionGroup: "",
  });

  const [callbackConfig, setCallbackConfig] = useState([]);
  const [actions, setActions] = useState([]);
  const [expandedActionIndices, setExpandedActionIndices] = useState({});
  const [editingActionIndex, setEditingActionIndex] = useState(null);
  const [expandedExpressions, setExpandedExpressions] = useState({});
  const [toast, setToast] = useState({ type: "", message: "" });
  const [nameInput, setNameInput] = useState(form.name || "");

  const [retryConfig, setRetryConfig] = useState({
    currentRetryCount: "",
    maxRetryCount: "",
    purpose: "",
  });

  const [toggles, setToggles] = useState({
    isMandatory: false,
    matchActionsForReuse: false,
    expireAssetIfValidFromIsNull: false,
    expireAssetIfValidToIsNull: false,
    uniquenessCheck: false,
  });

  const [showActionsModal, setShowActionsModal] = useState(false);
  const [errors, setErrors] = useState({});

  const {
    mutate: searchAssets,
    data: assetOptions = [],
  } = useAssetNameSearch();
  const searchAssetsRef = useRef(searchAssets);
  searchAssetsRef.current = searchAssets;

  useEffect(() => {
    if (nameInput.length >= 3) {
      searchAssetsRef.current(nameInput);
    }
  }, [nameInput]);

  const requiredFields = ["id", "name"];

  useEffect(() => {
    if (existingAsset) {
      setForm({
        id: existingAsset.id || "",
        name: existingAsset.name || "",
        defaultActionGroup: existingAsset.defaultActionGroup || "",
      });
      setActions(existingAsset.actions || []);
      setCallbackConfig(existingAsset.callbackConfig || []);
      setRetryConfig(
        existingAsset.retryConfig
          ? {
              ...existingAsset.retryConfig,
            }
          : {
              maxRetryCount: "",
              purpose: "",
            }
      );

      setToggles({
        isMandatory: existingAsset.isMandatory || false,
        matchActionsForReuse: existingAsset.matchActionsForReuse || false,
        expireAssetIfValidFromIsNull:
          existingAsset.expireAssetIfValidFromIsNull || false,
        expireAssetIfValidToIsNull:
          existingAsset.expireAssetIfValidToIsNull || false,
        uniquenessCheck: existingAsset.uniquenessCheck || false,
      });
    }
  }, [existingAsset]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleToggleChange = (field) => {
    setToggles((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleCallbackChange = (index, key, value) => {
    if (key === "onStatus") {
      const existingStatuses = callbackConfig
        .filter((_, i) => i !== index)
        .map((c) => c.onStatus);

      if (existingStatuses.includes(value)) {
        setToast({
          type: "error",
          message: `"${value}" status is already selected.`,
        });
        return;
      }
    }

    const updated = [...callbackConfig];
    updated[index][key] = value;
    setCallbackConfig(updated);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`callback_onStatus_${index}`];
      return newErrors;
    });
  };

  const addCallbackConfig = () => {
    setCallbackConfig((prev) => [...prev, { callbackUrl: "", onStatus: "" }]);
  };

  const removeCallbackConfig = (index) => {
    const updated = callbackConfig.filter((_, i) => i !== index);
    setCallbackConfig(updated);
  };

  const handleRetryChange = (field, value) => {
    setRetryConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleActionSave = (newAction) => {
    if (editingActionIndex !== null) {
      const updated = [...actions];
      updated[editingActionIndex] = newAction;
      setActions(updated);
      setEditingActionIndex(null);
    } else {
      setActions((prev) => [...prev, newAction]);
    }
    setShowActionsModal(false);
    setErrors((prev) => ({ ...prev, actions: "" }));
  };

  const toggleActionCollapse = (index) => {
    setExpandedActionIndices((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const toggleExpressionExpand = (assetIndex, actionIndex) => {
    const key = `${assetIndex}-${actionIndex}`;
    setExpandedExpressions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveAsset = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!form[field]) {
        newErrors[field] = "This field is required";
      }
    });

    if (actions.length === 0) {
      newErrors.actions = "At least one action is required";
    }

    if (
      form.defaultActionGroup &&
      !actions.some((action) => action.actionGroup === form.defaultActionGroup)
    ) {
      newErrors.actionGroup = `At least one action must have Action Group as "${form.defaultActionGroup}"`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const asset = {
      ...form,
      actions,
      callbackConfig,
      retryConfig: {
        ...retryConfig,
      },
      ...toggles,
    };

    onSaveAsset(asset);
    onClose();
  };

  return (
    <div className="assets-modal-overlay">
      {toast.message && (
        <GeneralToast type={toast.type} message={toast.message} />
      )}

      <div className="assets-modal-box">
        <div className="assets-modal-header">
          <h3 className="assets-modal-title">Add Asset</h3>
          <span className="assets-modal-close" onClick={onClose}>
            ×
          </span>
        </div>

        <div className="assets-modal-content">
          <div className="assets-modal-field-row">
            <label>
              Id <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              placeholder="Asset ID"
              value={form.id}
              readOnly
            />
          </div>
          {errors.id && <div className="input-error-message">{errors.id}</div>}

          <div className="assets-modal-field-row">
            <label>
              Name <span style={{ color: "red" }}>*</span>
            </label>
            <div style={{ width: "60%" }}>
              <Select
                inputValue={nameInput}
                value={
                  form.id && form.name
                    ? { label: form.name, value: form.id }
                    : null
                }
                onInputChange={(val, { action }) => {
                  if (action === "input-change") {
                    setNameInput(val);
                  }
                  if (action === "clear") {
                    setNameInput("");
                    handleChange("name", "");
                    handleChange("id", "");
                  }
                }}
                onChange={(selected) => {
                  if (selected) {
                    handleChange("name", selected.label);
                    handleChange("id", selected.value);
                    setNameInput("");
                  } else {
                    setNameInput("");
                    handleChange("name", "");
                    handleChange("id", "");
                  }
                }}
                options={assetOptions.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                placeholder="Search by asset name"
                classNamePrefix="react-select"
                isClearable={true}
                isSearchable={true}
                noOptionsMessage={() =>
                  nameInput.length < 3
                    ? "Type at least 3 characters"
                    : "No matching assets"
                }
                loadingMessage={() => "Loading..."}
              />
            </div>
          </div>

          {errors.name && (
            <div className="input-error-message">{errors.name}</div>
          )}

          {Object.keys(toggles).map((key, index) => (
            <div className="assets-modal-field-row" key={index}>
              <label>
                {key}
                {["isMandatory", "uniquenessCheck"].includes(key) && (
                  <span style={{ color: "red" }}> *</span>
                )}
              </label>
              <label className="assets-crt-toggle small">
                <input
                  type="checkbox"
                  checked={toggles[key]}
                  onChange={() => handleToggleChange(key)}
                />
                <span className="assets-crt-slider round"></span>
              </label>
            </div>
          ))}

          <div className="assets-modal-field-row">
            <label>defaultActionGroup</label>
            <input
              type="text"
              placeholder="Enter group"
              value={form.defaultActionGroup}
              onChange={(e) =>
                handleChange("defaultActionGroup", e.target.value)
              }
            />
          </div>
          {errors.actionGroup && (
            <div className="input-error-message">{errors.actionGroup}</div>
          )}

          <div className="assets-modal-field-row">
            <label>retryConfig</label>
          </div>

          <div className="retry-config-group">
            <div className="retry-config-line">
              <span className="retry-dot">.</span>
              <label>maxRetryCounty</label>
              <input
                type="number"
                value={retryConfig.maxRetryCount}
                onChange={(e) =>
                  handleRetryChange("maxRetryCount", e.target.value)
                }
              />
            </div>

            <div className="retry-config-line">
              <span className="retry-dot">.</span>
              <label>purpose</label>
              <input
                type="text"
                value={retryConfig.purpose}
                onChange={(e) => handleRetryChange("purpose", e.target.value)}
              />
            </div>
          </div>

          <div className="assets-modal-field-row">
            <label>Callback Config</label>
            <button className="assets-crt-add-btn" onClick={addCallbackConfig}>
              +
            </button>
          </div>

          {callbackConfig.map((entry, index) => (
            <div key={index} className="assets-config-wrapper">
              <div className="assets-config-column">
                <label className="assets-config-label">Callback URL</label>
                <input
                  className="assets-modal-config-input"
                  placeholder="Callback URL"
                  value={entry.callbackUrl}
                  onChange={(e) =>
                    handleCallbackChange(index, "callbackUrl", e.target.value)
                  }
                />
              </div>

              <div className="assets-config-column">
                <label className="assets-config-label">
                  onStatus <span style={{ color: "red" }}>*</span>
                </label>
                <div style={{ width: "100%" }}>
                  <Select
                    value={OnStatusOptions.find(
                      (option) => option.value === entry.onStatus
                    )}
                    onChange={(selected) =>
                      handleCallbackChange(
                        index,
                        "onStatus",
                        selected?.value || ""
                      )
                    }
                    options={OnStatusOptions.map((option) => ({
                      ...option,
                      isDisabled: callbackConfig
                        .filter((_, i) => i !== index)
                        .map((c) => c.onStatus)
                        .includes(option.value),
                    }))}
                    classNamePrefix="react-select"
                  />
                </div>
              </div>

              <div className="assets-config-remove">
                <button
                  className="assets-crt-remove-btn"
                  onClick={() => removeCallbackConfig(index)}
                >
                  -
                </button>
              </div>
            </div>
          ))}

          <div className="assets-modal-field-row">
            <label>
              Actions <span style={{ color: "red" }}>*</span>
            </label>
            <button
              className="assets-crt-add-btn"
              onClick={() => setShowActionsModal(true)}
            >
              +
            </button>
          </div>
          {errors.actions && (
            <div className="input-error-message">{errors.actions}</div>
          )}

          {actions.length > 0 && (
            <>
              <h5 className="text-base font-semibold mb-2">Actions</h5>
              <div>
                {actions.map((action, index) => {
                  const isExpanded = expandedActionIndices[index];
                  return (
                    <div className="asset-action-summary" key={index}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                          backgroundColor: "#f3f4f6",
                          padding: "8px 12px",
                          borderRadius: "6px",
                        }}
                        onClick={() => toggleActionCollapse(index)}
                      >
                        <strong>
                          {isExpanded ? "▼" : "▶"}{" "}
                          <span style={{ color: "#0e76a8" }}>Name:</span>{" "}
                          <span style={{ fontWeight: 400 }}>
                            {action.name || "Unnamed Action"}
                          </span>
                        </strong>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            fontSize: "16px",
                          }}
                        >
                          <FaEdit
                            title="Edit"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingActionIndex(index);
                              setShowActionsModal(true);
                            }}
                          />
                          <FaTrash
                            title="Delete"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const updated = actions.filter(
                                (_, i) => i !== index
                              );
                              setActions(updated);
                            }}
                          />
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{ marginTop: "10px" }}>
                          <div className="asset-action-summary-item">
                            <span className="asset-label">Name:</span>
                            <span className="asset-value">{action.name}</span>
                          </div>
                          <div className="asset-action-summary-item">
                            <span className="asset-label">Initiator Type:</span>
                            <span className="asset-value">
                              {action.initiatorType}
                            </span>
                          </div>
                          <div className="asset-action-summary-item">
                            <span className="asset-label">Operation:</span>
                            <span className="asset-value">
                              {action.operation}
                            </span>
                          </div>
                          <div className="asset-action-summary-item">
                            <span className="asset-label">Mode:</span>
                            <span className="asset-value">{action.mode}</span>
                          </div>
                          <div className="asset-action-summary-item">
                            <span className="asset-label">Input Type:</span>
                            <span className="asset-value">
                              {action.inputType}
                            </span>
                          </div>
                          <div className="asset-action-summary-item">
                            <span className="asset-label">Provider:</span>
                            <span className="asset-value">
                              {action.provider}
                            </span>
                          </div>
                          {action.group && (
                            <div className="asset-action-summary-item">
                              <span className="asset-label">Group:</span>
                              <span className="asset-value">
                                {action.group}
                              </span>
                            </div>
                          )}
                          {action.actionGroup && (
                            <div className="asset-action-summary-item">
                              <span className="asset-label">Action Group:</span>
                              <span className="asset-value">
                                {action.actionGroup}
                              </span>
                            </div>
                          )}
                          {action.event?.callbackUrl && (
                            <div className="asset-action-summary-item">
                              <span className="asset-label">Callback URL:</span>
                              <span className="asset-value">
                                {action.event.callbackUrl}
                              </span>
                            </div>
                          )}

                          {Array.isArray(action.event?.eventData) &&
                            action.event.eventData.length > 0 && (
                              <div className="asset-action-summary-item">
                                <span className="asset-label">Event Data:</span>
                                <div className="asset-value">
                                  {action.event.eventData.map((entry, idx) => (
                                    <div
                                      key={idx}
                                      style={{ marginBottom: "6px" }}
                                    >
                                      {entry.onStatus && (
                                        <div>• onStatus: {entry.onStatus}</div>
                                      )}
                                      {entry.entity && (
                                        <div>• entity: {entry.entity}</div>
                                      )}
                                      {entry.entityId && (
                                        <div>• entityId: {entry.entityId}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {(action.digitalAssetUpdateConfig?.ruleConfig?.name ||
                            action.digitalAssetUpdateConfig?.ruleConfig
                              ?.evaluationMode ||
                            (action.digitalAssetUpdateConfig?.ruleConfig
                              ?.expressions?.length ?? 0) > 0 ||
                            action.digitalAssetUpdateConfig
                              ?.serviceConfigName) && (
                            <div
                              className="asset-action-summary-group"
                              style={{ marginTop: "1rem" }}
                            >
                              <strong
                                style={{
                                  display: "block",
                                  marginBottom: "6px",
                                }}
                              >
                                Digital Asset Update Config:
                              </strong>

                              {action.digitalAssetUpdateConfig.ruleConfig
                                ?.name && (
                                <div className="asset-action-summary-item">
                                  <span className="asset-label">
                                    • RuleConfig Name:
                                  </span>
                                  <span className="asset-value">
                                    {
                                      action.digitalAssetUpdateConfig.ruleConfig
                                        .name
                                    }
                                  </span>
                                </div>
                              )}

                              {action.digitalAssetUpdateConfig.ruleConfig
                                ?.evaluationMode && (
                                <div className="asset-action-summary-item">
                                  <span className="asset-label">
                                    • Evaluation Mode:
                                  </span>
                                  <span className="asset-value">
                                    {
                                      action.digitalAssetUpdateConfig.ruleConfig
                                        .evaluationMode
                                    }
                                  </span>
                                </div>
                              )}

                              {action.digitalAssetUpdateConfig.ruleConfig
                                ?.expressions?.length > 0 && (
                                <div className="asset-action-summary-item">
                                  <strong
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      toggleExpressionExpand(index, index)
                                    }
                                  >
                                    {expandedExpressions[`${index}-${index}`]
                                      ? "▼"
                                      : "▶"}{" "}
                                    Expressions
                                  </strong>

                                  {expandedExpressions[`${index}-${index}`] && (
                                    <div style={{ marginTop: "0.5rem" }}>
                                      {action.digitalAssetUpdateConfig.ruleConfig.expressions.map(
                                        (exp, k) => (
                                          <div
                                            key={k}
                                            className="expression-block"
                                          >
                                            <div>Type: {exp.type}</div>
                                            <div>LHS: {exp.lhs}</div>
                                            <div>RHS: {exp.rhs}</div>
                                            {exp.validationFailureMessage && (
                                              <div>
                                                Msg:{" "}
                                                {exp.validationFailureMessage}
                                              </div>
                                            )}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {action.digitalAssetUpdateConfig
                                .serviceConfigName && (
                                <div className="asset-action-summary-item">
                                  <span className="asset-label">
                                    • Service Config Name:
                                  </span>
                                  <span className="asset-value">
                                    {
                                      action.digitalAssetUpdateConfig
                                        .serviceConfigName
                                    }
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <div className="assets-modal-actions">
            <button className="assets-modal-save" onClick={handleSaveAsset}>
              Save
            </button>
            <button className="assets-modal-cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>

        {showActionsModal && (
          <ActionsModal
            onClose={() => {
              setShowActionsModal(false);
              setEditingActionIndex(null);
            }}
            onSaveAction={handleActionSave}
            existingAction={
              editingActionIndex !== null ? actions[editingActionIndex] : null
            }
          />
        )}
      </div>
    </div>
  );
};

export default AssetsModal;
